/*
 * Copyright (c) Nexign, JSC, 2020-2022.
 */
/**
 * PageSettings SideMenu Component
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @version 1.16.1
 * @since 1.3.0
 */

import { Fragment, useCallback, useMemo } from 'react';

import { LinkBlock, SideMenuItem } from '../../../components/common';
import { ISideMenuItem } from '../../../components/common/SideMenu/SideMenuItem';
import { IPageSettingsTranslation } from '../../../modules/internationalization/types/pages/pageSettings';
import { ONBOARDING_DATA_ATTR } from '../../../modules/onboarding/onboardingConstants';
import { EOnboardingComponentsId } from '../../../modules/onboarding/types/onboardingTypes';
import { AccessComponentKeyEnum } from '../../../modules/role/constants/guard/guardConstants';
import Guard from '../../../modules/role/Guard';
import { mapSettingsMenuItems, SettingsMenuItems } from '../PageSettingsTypes';

export interface IPageSettingsTitleProps {
    activeItemValue: string;
    settingsTranslation: IPageSettingsTranslation;
}

export interface IPageSettingsTabMenu extends Pick<ISideMenuItem, 'title'> {
    accessKey?: AccessComponentKeyEnum;
    onboarding?: EOnboardingComponentsId;
    url: string;
    value: string;
}

const PageSettingsSideMenu = ({
                                  settingsTranslation,
                                  activeItemValue,
                              }: IPageSettingsTitleProps) => {


    const sideMenuItems: IPageSettingsTabMenu[] = useMemo(() => Object.values(SettingsMenuItems).map(name => ({
            accessKey: mapSettingsMenuItems[name]?.accessKey,
            onboarding: mapSettingsMenuItems[name]?.onboarding,
            title: settingsTranslation.sideMenuItemNames[name],
            url: mapSettingsMenuItems[name]?.url,
            value: name,
        }))
        , [settingsTranslation]);

    const renderSideMenu = useCallback((item: IPageSettingsTabMenu) => (
        <div {...{ [ONBOARDING_DATA_ATTR]: item.onboarding }}>
            <LinkBlock key={item.url} url={item.url}>
                <SideMenuItem
                    id={item.url}
                    isActive={item.value === activeItemValue}
                    level={0}
                    title={item.title}
                />
            </LinkBlock>
        </div>
    ), [activeItemValue]);

    return <Fragment>
        {sideMenuItems.map(item => item.accessKey
            ? <Guard accessKey={item.accessKey}>{renderSideMenu(item)}</Guard>
            : renderSideMenu(item))
        }
    </Fragment>;
};

export default PageSettingsSideMenu;
